import React, { useState, useEffect, useRef, useCallback, useLayoutEffect, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiShield, FiClock, FiFlag, FiChevronLeft, FiChevronRight, FiInfo, FiCheckCircle, FiFileText, FiArrowRight, FiTrash2 } from 'react-icons/fi';
import '../../../styles/Student.css';
import '../dashboard/assessment.css';
import '../assessment-module-mobile.css';
import testService from '../../../services/testService';
import { useUser } from '../../../contexts/UserContext';
import { isAssessmentFlagOn, buildIntroVideoPlayUrl, getIntroVideoPlayUrlCandidates, normalizeIntroVideosList } from '../../../utils/assessmentStudent';
import toast from 'react-hot-toast';
import { useAssessmentTooltips } from '../AssessmentContext';

const getTestIntroVideosList = (assessment) => {
    if (!assessment) return [];
    const raw = assessment.intro_videos || assessment.videoFiles || [];
    let parsed = raw;
    if (typeof raw === 'string') {
        try {
            parsed = JSON.parse(raw);
        } catch (e) {
            const trimmed = raw.trim();
            parsed = trimmed ? [trimmed] : [];
        }
    }
    if (!Array.isArray(parsed)) {
        parsed = parsed ? [parsed] : [];
    }
    return parsed;
};

const parseTestInstructions = (instructions) => {
    const raw = typeof instructions === 'string' ? instructions.trim() : '';
    if (!raw) {
        return [];
    }

    const lines = raw
        .split(/\r?\n/)
        .map((line) => line.replace(/^[\s•\-*]+/, '').trim())
        .filter(Boolean);

    return lines.length > 0 ? lines : [raw];
};

const resolveQuestionPedagogy = (question) => {
    const pedagogy = String(question?.pedagogy || question?.knowledge_type || '').trim();
    return pedagogy || null;
};

const isProctoredExam = (data) => {
    if (!data) return false;
    return (
        isAssessmentFlagOn(data.assessment?.proctored_exam) ||
        isAssessmentFlagOn(data.pack?.proctored_exam)
    );
};

const isBrowserFullscreen = () => {
    const doc = document;
    return !!(
        doc.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.mozFullScreenElement ||
        doc.msFullscreenElement
    );
};

const FullscreenGate = ({ onContinue, mode = 'start' }) => {
    const isStart = mode === 'start';

    return (
        <div
            className={`assess-mod-fullscreen-gate ${isStart ? 'assess-mod-fullscreen-gate--page' : 'assess-mod-fullscreen-gate--overlay'
                }`}
        >
            <div className="assess-mod-fullscreen-gate-card">
                <div className="assess-mod-fullscreen-gate-icon" aria-hidden>
                    <FiShield size={36} />
                </div>
                <h1 className="assess-mod-fullscreen-gate-title">Fullscreen Required</h1>
                <p className="assess-mod-fullscreen-gate-desc">
                    {isStart
                        ? 'This exam runs in fullscreen mode. Click below to start — your browser will ask you to allow fullscreen.'
                        : 'You left fullscreen mode. Re-enter fullscreen to continue your exam.'}
                </p>
                <button type="button" className="assess-mod-fullscreen-gate-btn" onClick={onContinue}>
                    <span className="assess-mod-fullscreen-gate-btn-text">
                        {isStart ? 'Enter Fullscreen & Start Exam' : 'Enter Fullscreen'}
                    </span>
                    <span className="assess-mod-fullscreen-gate-btn-icon">
                        <FiArrowRight size={18} />
                    </span>
                </button>
            </div>
        </div>
    );
};

const enterExamFullscreen = async () => {
    if (isBrowserFullscreen()) return true;

    const el = document.documentElement;
    const request =
        el.requestFullscreen ||
        el.webkitRequestFullscreen ||
        el.mozRequestFullScreen ||
        el.msRequestFullscreen;

    if (!request) {
        toast.error('Fullscreen is not supported in this browser.');
        return false;
    }

    try {
        await request.call(el);
        return true;
    } catch (err) {
        console.warn('Fullscreen request failed:', err);
        toast.error('Click the button below to allow fullscreen (browser requires your click).');
        return false;
    }
};

const TestExecutionSidebar = memo(function TestExecutionSidebar({
    testInstructions,
    answeredCount,
    questions,
    currentIdx,
    answers,
    flagged,
    onSelectQuestion,
}) {
    const navigatorScrollRef = useRef(null);
    const savedScrollTopRef = useRef(0);

    const handleNavigatorScroll = useCallback(() => {
        if (navigatorScrollRef.current) {
            savedScrollTopRef.current = navigatorScrollRef.current.scrollTop;
        }
    }, []);

    useLayoutEffect(() => {
        const el = navigatorScrollRef.current;
        if (el) {
            el.scrollTop = savedScrollTopRef.current;
        }
    });

    useEffect(() => {
        const el = navigatorScrollRef.current;
        if (!el) return undefined;

        const currentBtn = el.querySelector('.nav-btn.current');
        if (!currentBtn) return undefined;

        const frame = requestAnimationFrame(() => {
            currentBtn.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
            savedScrollTopRef.current = el.scrollTop;
        });

        return () => cancelAnimationFrame(frame);
    }, [currentIdx]);

    return (
        <div className="assess-mod-student-exec-sidebar execution-sidebar">
            <div className="instructions-panel">
                <div className="panel-title-row">
                    <FiInfo className="panel-icon" />
                    <span>TEST INSTRUCTIONS</span>
                </div>
                {testInstructions.length > 0 ? (
                    <ul className="instruction-list">
                        {testInstructions.map((item, idx) => (
                            <li key={idx}>{item}</li>
                        ))}
                    </ul>
                ) : (
                    <p className="instruction-empty">No test instructions provided for this test.</p>
                )}
            </div>

            <div className="progress-panel">
                <div className="panel-sub-title">OVERALL PROGRESS</div>
                <div className="progress-main-row">
                    <div className="progress-count">{answeredCount} of {questions.length}</div>
                    <div className="progress-label">Answered</div>
                </div>
                <div className="progress-bar-container">
                    <div
                        className="progress-bar-fill"
                        style={{ width: `${questions.length ? (answeredCount / questions.length) * 100 : 0}%` }}
                    />
                </div>
                <div className="flagged-count-row">
                    <span className="flag-val">{flagged.size}</span>
                    <span className="flag-lbl">FLAGGED</span>
                </div>
            </div>

            <div className="navigator-panel">
                <div className="panel-sub-title">QUESTION NAVIGATOR</div>
                <div
                    ref={navigatorScrollRef}
                    className="navigator-grid-scroll"
                    onScroll={handleNavigatorScroll}
                >
                    <div className="navigator-grid">
                        {questions.map((q, idx) => {
                            const isCurrent = currentIdx === idx;
                            const isAnswered = answers[q.id];
                            const isFlagged = flagged.has(idx);

                            let navClass = 'nav-btn';
                            if (isCurrent) navClass += ' current';
                            if (isAnswered) navClass += ' answered';
                            if (isFlagged) navClass += ' flagged';

                            return (
                                <button
                                    key={q.id ?? idx}
                                    type="button"
                                    className={navClass}
                                    onClick={() => onSelectQuestion(idx)}
                                >
                                    {idx + 1}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="navigator-legend">
                    <div className="legend-item"><span className="dot current" /> CURRENT</div>
                    <div className="legend-item"><span className="dot answered" /> ANSWERED</div>
                    <div className="legend-item"><span className="dot flagged" /> FLAGGED</div>
                    <div className="legend-item"><span className="dot unanswered" /> UNANSWERED</div>
                </div>
            </div>
        </div>
    );
});

const TestExecutionPage = () => {
    useAssessmentTooltips();
    const { testId, packId } = useParams();
    const navigate = useNavigate();
    const { user } = useUser();

    const [loading, setLoading] = useState(true);
    const [testData, setTestData] = useState(null);
    const [questions, setQuestions] = useState([]);

    // Workflow state: 'loading' | 'video' | 'taking' | 'uploading' | 'completing' | 'success'
    const [view, setView] = useState('loading');
    const [currentIdx, setCurrentIdx] = useState(0);
    const [answers, setAnswers] = useState({});
    const [flagged, setFlagged] = useState(new Set());
    const [timeLeft, setTimeLeft] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionFiles, setSubmissionFiles] = useState([]);
    const [uploadError, setUploadError] = useState(false);
    const fileInputRef = useRef(null);
    const SUBMISSION_MAX_BYTES = 2 * 1024 * 1024;
    const [startedAt] = useState(new Date().toLocaleString('sv-SE'));
    const [videoFinished, setVideoFinished] = useState(false);
    const [videoRef] = useState(React.createRef());
    const [lastTime, setLastTime] = useState(0);
    const [needsFullscreen, setNeedsFullscreen] = useState(false);
    const wasFullscreenRef = React.useRef(false);
    const examDeadlineMsRef = useRef(null);
    const autoSubmittedRef = useRef(false);

    const exitFullscreen = React.useCallback(() => {
        const doc = document;
        if (!doc.fullscreenElement && !doc.webkitFullscreenElement && !doc.mozFullScreenElement && !doc.msFullscreenElement) {
            return;
        }
        const exit =
            doc.exitFullscreen ||
            doc.webkitExitFullscreen ||
            doc.mozCancelFullScreen ||
            doc.msExitFullscreen;
        if (exit) {
            exit.call(doc).catch(() => { });
        }
    }, []);

    const getRemainingSeconds = useCallback(() => {
        const deadline = examDeadlineMsRef.current;
        if (!deadline) return 0;
        return Math.max(0, Math.floor((deadline - Date.now()) / 1000));
    }, []);

    const submitFinal = useCallback(async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        // Calculate basic scores for storage
        const mcqScore = questions
            .filter(q => q.type !== 'subjective')
            .reduce((score, q) => {
                const userAnswer = answers[q.id];
                if (!userAnswer) return score;

                // Compare user answer text with the text of the correct option letter
                const correctText = q.optionMap[q.correctAnswer];
                const isCorrect = userAnswer === correctText;

                return score + (isCorrect ? q.marks : 0);
            }, 0);

        const totalMarks = questions.reduce((sum, q) => sum + q.marks, 0);
        const overallPct = totalMarks > 0 ? Math.round((mcqScore / totalMarks) * 100) : 0;

        try {
            const pubStr = localStorage.getItem('published_tests');
            let isLocalTest = false;
            if (pubStr) {
                try {
                    const parsed = JSON.parse(pubStr);
                    isLocalTest = parsed.some(t => (t.groups || []).some(g => String(g.id) === String(packId)));
                } catch (e) { }
            }

            if (isLocalTest) {
                const submissionKey = `sub-completed-${testId}-${packId}`;
                localStorage.setItem(submissionKey, 'COMPLETED');
                window.dispatchEvent(new Event('storage'));
            } else {
                // 1. Save to Database
                await testService.saveSubmission({
                    assessment_id: testId,
                    test_pack_id: packId,
                    user_id: user?.user_id || 1,
                    answers: answers,
                    mcq_score: mcqScore,
                    overall_pct: overallPct,
                    status: 'Completed',
                    started_at: startedAt
                }, submissionFiles);
            }

            autoSubmittedRef.current = true;
            exitFullscreen();
            sessionStorage.removeItem(`video_watched_${testId}`);

            // Clear draft answers and state from localStorage
            localStorage.removeItem(`test_draft_answers_${packId}`);
            localStorage.removeItem(`test_draft_flagged_${packId}`);
            localStorage.removeItem(`test_draft_current_idx_${packId}`);

            setView('success');
        } catch (err) {
            console.error("Database submission failed:", err);
            autoSubmittedRef.current = false;
            const serverMsg = err?.response?.data?.message;
            toast.error(
                serverMsg || err?.message || 'Failed to submit test. Please check your connection and try again.'
            );
        } finally {
            setIsSubmitting(false);
        }
    }, [answers, exitFullscreen, isSubmitting, packId, questions, startedAt, submissionFiles, testId, user?.user_id]);

    const handleTimeExpired = useCallback(() => {
        if (autoSubmittedRef.current || isSubmitting) return;

        const activeViews = ['taking', 'uploading', 'completing', 'video', 'prep'];
        if (!activeViews.includes(view)) return;

        autoSubmittedRef.current = true;
        toast('Time is up! Submitting your test automatically.', { icon: '⏱️' });
        submitFinal();
    }, [isSubmitting, submitFinal, view]);

    const startTakingView = React.useCallback(async () => {
        if (isProctoredExam(testData)) {
            const entered = await enterExamFullscreen();
            if (!entered) {
                setNeedsFullscreen(true);
                return;
            }
            wasFullscreenRef.current = true;
            setNeedsFullscreen(false);
        }
        setView('taking');
    }, [testData]);

    const getFileIcon = (filename) => {
        const name = (typeof filename === 'object' && filename) ? filename.name : filename;
        if (!name) return 'bi-file-earmark';
        const ext = name.split(/[?#]/)[0].split('.').pop().toLowerCase();
        if (['mp4', 'webm', 'mov', 'avi', 'mkv', 'mpeg', 'mpg', 'ogv', 'm4v'].includes(ext)) return 'bi-play-fill';
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'bi-image';
        if (ext === 'pdf') return 'bi-file-pdf';
        if (['doc', 'docx'].includes(ext)) return 'bi-file-earmark-word';
        if (['xls', 'xlsx', 'csv'].includes(ext)) return 'bi-file-earmark-excel';
        if (['ppt', 'pptx'].includes(ext)) return 'bi-file-earmark-ppt';
        if (ext === 'txt') return 'bi-file-earmark-text';
        return 'bi-file-earmark';
    };

    const getFileType = (url) => {
        if (!url) return 'unknown';
        if (typeof url === 'object' && url) {
            if (url.type && url.type.startsWith('video/')) return 'video';
            if (url.type && url.type.startsWith('image/')) return 'image';
            if (url.type && url.type.includes('pdf')) return 'pdf';
            return 'document';
        }
        try {
            const path = url.split(/[?#]/)[0];
            const ext = path.split('.').pop().toLowerCase();
            if (['mp4', 'webm', 'mov', 'avi', 'mkv', 'mpeg', 'mpg', 'ogv', 'm4v'].includes(ext)) return 'video';
            if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
            if (ext === 'pdf') return 'pdf';
            return 'document';
        } catch (e) {
            return 'document';
        }
    };

    const VideoInstructionView = ({ files, videoFinished, setVideoFinished, onStartExam, videoRef, lastTime, setLastTime }) => {
        const [activeFileIdx, setActiveFileIdx] = useState(0);
        const activeFile = files[activeFileIdx];
        const fileType = getFileType(activeFile);
        const [resolvedUrl, setResolvedUrl] = useState('');
        const [urlAttempt, setUrlAttempt] = useState(0);
        const [candidates, setCandidates] = useState([]);

        useEffect(() => {
            setUrlAttempt(0);
        }, [activeFileIdx]);

        useEffect(() => {
            let objectUrl = '';

            if (activeFile && typeof activeFile === 'object') {
                if (activeFile.objectUrl) {
                    setResolvedUrl(activeFile.objectUrl);
                    setCandidates([activeFile.objectUrl]);
                } else {
                    // Fallback: see if the File object is in memory
                    const fileObj = window.selectedVideoFiles?.[activeFile.name];
                    if (fileObj) {
                        try {
                            objectUrl = URL.createObjectURL(fileObj);
                            setResolvedUrl(objectUrl);
                            setCandidates([objectUrl]);
                        } catch (e) {
                            setResolvedUrl('');
                            setCandidates([]);
                        }
                    } else {
                        setResolvedUrl('');
                        setCandidates([]);
                    }
                }
            } else if (activeFile) {
                const apiBase = testService.getBaseURL();
                const urlCandidates = getIntroVideoPlayUrlCandidates(activeFile, apiBase);
                setCandidates(urlCandidates);
                setResolvedUrl(urlCandidates[urlAttempt] || buildIntroVideoPlayUrl(activeFile, apiBase));
            } else {
                setResolvedUrl('');
                setCandidates([]);
            }

            return () => {
                if (objectUrl) {
                    URL.revokeObjectURL(objectUrl);
                }
            };
        }, [activeFile, urlAttempt]);

        const handleMediaError = (e) => {
            const mediaError = e?.target?.error;
            if (mediaError && mediaError.code === 1) {
                return;
            }
            const nextAttempt = urlAttempt + 1;
            if (nextAttempt < candidates.length) {
                setUrlAttempt(nextAttempt);
            }
        };

        const handleTimeUpdate = () => {
            if (videoRef.current) {
                if (videoRef.current.currentTime > lastTime + 2) {
                    videoRef.current.currentTime = lastTime;
                } else {
                    setLastTime(videoRef.current.currentTime);
                }
            }
        };

        useEffect(() => {
            if (activeFile && fileType !== 'video') {
                setVideoFinished(true);
            }
        }, [activeFile, fileType, setVideoFinished]);

        const handleNextFile = () => {
            if (activeFileIdx < files.length - 1) {
                setActiveFileIdx(prev => prev + 1);
            }
        };

        const handlePrevFile = () => {
            if (activeFileIdx > 0) {
                setActiveFileIdx(prev => prev - 1);
            }
        };

        const activeFileName = typeof activeFile === 'object' && activeFile ? activeFile.name : (activeFile ? activeFile.split('/').pop() : '');

        return (
            <div className="video-instruction-container">
                <div className="video-modal max-w-5xl">
                    <div className="video-header">
                        <div className="flex flex-col">
                            <h2 className="video-title">Test Materials</h2>
                            <p className="video-subtitle">Please review all attached files before starting your test.</p>
                        </div>
                        <div className="video-badge">MANDATORY</div>
                    </div>

                    <div className="flex flex-col lg:flex-row gap-6 p-6 min-h-[550px]">
                        <div className="flex-1 flex flex-col gap-4">
                            <div className="flex-1 bg-slate-900 rounded-2xl overflow-hidden flex items-center justify-center relative min-h-[450px]">
                                {resolvedUrl ? (
                                    <>
                                        {fileType === 'video' && (
                                            <video
                                                key={resolvedUrl}
                                                ref={videoRef}
                                                src={resolvedUrl}
                                                className="w-full h-full object-contain"
                                                controls
                                                onError={handleMediaError}
                                                onTimeUpdate={handleTimeUpdate}
                                                onEnded={() => setVideoFinished(true)}
                                                controlsList="nodownload"
                                                onContextMenu={e => e.preventDefault()}
                                            />
                                        )}
                                        {fileType === 'image' && (
                                            <img src={resolvedUrl} alt="Instructional Material" className="w-full h-full object-contain" />
                                        )}
                                        {fileType === 'pdf' && (
                                            <div className="w-full h-full flex flex-col bg-white">
                                                <iframe
                                                    src={`${resolvedUrl}#toolbar=0&navpanes=0`}
                                                    className="flex-1 w-full border-0"
                                                    title="PDF Viewer"
                                                />
                                                <div className="p-3 bg-slate-100 border-t flex justify-between items-center">
                                                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Embedded PDF Viewer</span>
                                                    <a href={resolvedUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] font-black text-red-600 uppercase hover:underline">
                                                        Open Fullscreen
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                        {fileType === 'document' && (
                                            <div className="flex flex-col items-center gap-6 text-white p-10 text-center">
                                                <div className="w-24 h-24 rounded-3xl bg-white/5 flex items-center justify-center border border-white/10 shadow-2xl">
                                                    <i className={`bi ${getFileIcon(activeFile)} text-5xl text-red-500`}></i>
                                                </div>
                                                <div>
                                                    <p className="text-lg font-bold mb-1">{activeFileName}</p>
                                                    <p className="text-sm opacity-50 mb-6 uppercase tracking-widest font-black">Document Attachment</p>

                                                    <div className="flex flex-col items-center gap-4">
                                                        <a href={resolvedUrl} target="_blank" rel="noopener noreferrer" className="px-8 py-3 bg-red-600 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-red-700 transition-all shadow-lg hover:shadow-red-500/25">
                                                            Download & View Document
                                                        </a>
                                                        <p className="text-[10px] text-slate-500 max-w-xs leading-relaxed">
                                                            This file type cannot be displayed directly in the browser.
                                                            Please download it to review the instructions.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="text-slate-500 font-bold uppercase tracking-widest text-[10px]">No materials available</div>
                                )}
                            </div>

                            {/* Inner Navigation */}
                            {files.length > 1 && (
                                <div className="flex items-center justify-between px-2">
                                    <button
                                        onClick={handlePrevFile}
                                        disabled={activeFileIdx === 0}
                                        className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-all ${activeFileIdx === 0 ? 'text-slate-700 opacity-20 cursor-not-allowed' : 'text-slate-400 hover:text-red-500'}`}
                                    >
                                        <FiChevronLeft size={14} /> Previous
                                    </button>
                                    <div className="flex items-center gap-2">
                                        {files.map((_, i) => (
                                            <div
                                                key={i}
                                                className={`w-1.5 h-1.5 rounded-full transition-all ${i === activeFileIdx ? 'bg-red-600 w-4' : 'bg-slate-700'}`}
                                            />
                                        ))}
                                    </div>
                                    <button
                                        onClick={handleNextFile}
                                        disabled={activeFileIdx === files.length - 1}
                                        className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-all ${activeFileIdx === files.length - 1 ? 'text-slate-700 opacity-20 cursor-not-allowed' : 'text-slate-400 hover:text-red-500'}`}
                                    >
                                        Next <FiChevronRight size={14} />
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="w-full lg:w-72 flex flex-col gap-3">
                            <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest px-1">Resources ({files.length})</h4>
                            <div className="flex flex-col gap-2 max-h-[480px] overflow-y-auto pr-1 custom-scrollbar">
                                {files.map((url, idx) => {
                                    const type = getFileType(url);
                                    const displayName = typeof url === 'object' && url ? url.name : url.split('/').pop();
                                    return (
                                        <button
                                            key={idx}
                                            onClick={() => {
                                                setActiveFileIdx(idx);
                                                if (type === 'video') setVideoFinished(false);
                                                else setVideoFinished(true);
                                            }}
                                            className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${activeFileIdx === idx ? 'bg-red-50 border-red-200 ring-1 ring-red-200' : 'bg-white/5 border-white/5 hover:border-red-500/30'}`}
                                        >
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${activeFileIdx === idx ? 'bg-red-600 text-white' : 'bg-white/5 text-slate-400'}`}>
                                                <i className={`bi ${getFileIcon(url)} text-sm`}></i>
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className={`text-[10px] font-bold truncate ${activeFileIdx === idx ? 'text-red-700' : 'text-slate-200'}`}>
                                                    {displayName}
                                                </p>
                                                <p className="text-[8px] text-slate-500 font-black uppercase tracking-tighter">
                                                    {type.toUpperCase()}
                                                </p>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="video-footer border-t border-white/5">
                        <div className="flex items-center gap-3">
                            <div className={`status-indicator ${videoFinished ? 'completed' : 'pending'}`}></div>
                            <span className="status-text text-[10px] font-black uppercase tracking-widest opacity-70">
                                {videoFinished ? 'Ready to Begin' : 'Action Required'}
                            </span>
                        </div>
                        <div className="flex items-center gap-4">
                            {activeFileIdx < files.length - 1 && (
                                <button
                                    className="px-6 py-2 bg-white/5 text-slate-300 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-white/10 transition-all border border-white/5"
                                    onClick={handleNextFile}
                                >
                                    Review Next
                                </button>
                            )}
                            <button
                                className={`begin-test-btn ${!videoFinished ? 'disabled' : ''}`}
                                disabled={!videoFinished}
                                onClick={onStartExam}
                            >
                                {videoFinished ? 'Start Test' : 'Content Locked'}
                                <FiArrowRight size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                let res;
                const pubStr = localStorage.getItem('published_tests');
                let foundLocal = null;
                if (pubStr) {
                    try {
                        const parsed = JSON.parse(pubStr);
                        for (const test of parsed) {
                            const group = (test.groups || []).find(g => String(g.id) === String(packId));
                            if (group) {
                                foundLocal = { test, group };
                                break;
                            }
                        }
                    } catch (e) { }
                }

                if (foundLocal) {
                    const { test, group } = foundLocal;
                    res = {
                        status: 'success',
                        assessment: test,
                        pack: {
                            id: group.id,
                            duration: group.duration || 60,
                            ...group
                        },
                        template: {
                            name: test.template?.name || 'Local Template',
                            total_marks: test.template?.sections?.reduce((sum, s) => sum + (parseInt(s.question_count || s.count || 0) * parseInt(s.marks_each || s.marks || 0)), 0) || 100,
                            pass_mark: test.template?.passMark || 50,
                        },
                        sections: (test.template?.sections || []).map((s, idx) => ({
                            id: idx,
                            section_name: s.section_name || s.name || `Section ${idx + 1}`
                        })),
                        questions: (test.questionBank?.questions || []).map((q, idx) => ({
                            id: q.id || idx,
                            question: q.question || q.question_text || '',
                            option_a: q.option_a || q.option_A || '',
                            option_b: q.option_b || q.option_B || '',
                            option_c: q.option_c || q.option_C || '',
                            option_d: q.option_d || q.option_D || '',
                            correct_answer: q.correct_answer || q.answer || 'A',
                            marks: q.marks || 1,
                            section_idx: 0,
                            type: q.type || 'MCQ'
                        }))
                    };
                } else {
                    res = await testService.getTestExecutionData(packId);
                }

                if (res.status === 'success') {
                    setTestData(res);
                    const processedQuestions = res.questions.map(q => {
                        const section = res.sections.find(s => s.id == q.section_idx) || res.sections[q.section_idx];

                        // Create a trimmed mapping of letters to option text for easy comparison later
                        const optionMap = {
                            'A': (q.option_a || '').trim(),
                            'B': (q.option_b || '').trim(),
                            'C': (q.option_c || '').trim(),
                            'D': (q.option_d || '').trim()
                        };

                        return {
                            id: q.id,
                            text: q.question,
                            type: (String(q.type || '').toUpperCase() === 'DESCRIPTIVE' || !q.option_a || q.option_a.trim() === '') ? 'subjective' : (q.type === 'MCQ' ? 'single' : 'multi'),
                            options: [q.option_a, q.option_b, q.option_c, q.option_d]
                                .filter(o => o && o.trim() !== '')
                                .map(o => o.trim()),
                            correctAnswer: (q.correct_answer || '').trim().toUpperCase(),
                            optionMap: optionMap,
                            marks: parseInt(q.marks || 1),
                            sectionName: section ? section.section_name : 'General',
                            pedagogy: resolveQuestionPedagogy(q),
                        };
                    });
                    setQuestions(processedQuestions);

                    // Calculate timeLeft from the scheduled end_time so the test
                    // always closes at the exact scheduled wall-clock time,
                    // regardless of when the candidate started.
                    const scheduledDate = res.pack.scheduled_date;  // e.g. "2026-06-11"
                    const endTimeStr    = res.pack.end_time;         // e.g. "14:00:00"
                    const durationSecs  = parseInt(res.pack.duration || 60, 10) * 60;
                    let computedTimeLeft = durationSecs;
                    if (scheduledDate && endTimeStr) {
                        const endDateTime = new Date(`${scheduledDate}T${endTimeStr}`);
                        examDeadlineMsRef.current = endDateTime.getTime();
                        const remaining = Math.floor((endDateTime.getTime() - Date.now()) / 1000);
                        computedTimeLeft = remaining > 0 ? remaining : 0;
                    } else {
                        examDeadlineMsRef.current = Date.now() + durationSecs * 1000;
                    }
                    setTimeLeft(computedTimeLeft);

                    // Load saved answers draft if exists
                    const savedAnswersStr = localStorage.getItem(`test_draft_answers_${packId}`);
                    if (savedAnswersStr) {
                        try {
                            const parsedAnswers = JSON.parse(savedAnswersStr);
                            if (parsedAnswers && typeof parsedAnswers === 'object') {
                                setAnswers(parsedAnswers);
                            }
                        } catch (e) {
                            console.error("Failed to parse saved draft answers", e);
                        }
                    }

                    // Load saved flagged questions draft if exists
                    const savedFlaggedStr = localStorage.getItem(`test_draft_flagged_${packId}`);
                    if (savedFlaggedStr) {
                        try {
                            const parsedFlagged = JSON.parse(savedFlaggedStr);
                            if (Array.isArray(parsedFlagged)) {
                                setFlagged(new Set(parsedFlagged));
                            }
                        } catch (e) {
                            console.error("Failed to parse saved draft flagged questions", e);
                        }
                    }

                    // Load saved current index draft if exists
                    const savedCurrentIdx = localStorage.getItem(`test_draft_current_idx_${packId}`);
                    if (savedCurrentIdx) {
                        const parsedIdx = parseInt(savedCurrentIdx, 10);
                        if (!isNaN(parsedIdx) && parsedIdx >= 0) {
                            setCurrentIdx(parsedIdx);
                        }
                    }

                    const videoWatched = sessionStorage.getItem(`video_watched_${testId}`) === 'true';
                    const hasVideo =
                        !videoWatched &&
                        isAssessmentFlagOn(res.assessment?.add_video || res.assessment?.videoEnabled) &&
                        getTestIntroVideosList(res.assessment).length > 0;
                    const proctored = isProctoredExam(res);
                    if (hasVideo) {
                        setView('video');
                    } else if (proctored) {
                        setView('prep');
                    } else {
                        setView('taking');
                    }
                }
            } catch (err) {
                console.error("Failed to load test data:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [packId]);

    useEffect(() => {
        if (loading || !testData || !examDeadlineMsRef.current) return;
        if (view === 'loading' || view === 'success') return;

        const tick = () => {
            const next = getRemainingSeconds();
            setTimeLeft(next);
            if (next <= 0) {
                handleTimeExpired();
            }
        };

        tick();
        const timer = setInterval(tick, 1000);
        return () => clearInterval(timer);
    }, [loading, testData, view, getRemainingSeconds, handleTimeExpired]);

    // Enter fullscreen when proctored exam starts (same as employee flow)
    useEffect(() => {
        if (view !== 'taking' || !isProctoredExam(testData) || isBrowserFullscreen()) {
            return undefined;
        }

        let cancelled = false;
        (async () => {
            const entered = await enterExamFullscreen();
            if (!cancelled) {
                if (entered) {
                    wasFullscreenRef.current = true;
                }
                setNeedsFullscreen(!entered);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [view, testData]);

    // Re-prompt if user exits fullscreen during a proctored exam
    useEffect(() => {
        if (view !== 'taking' || !isProctoredExam(testData)) {
            setNeedsFullscreen(false);
            return undefined;
        }

        const handleFullscreenChange = () => {
            const active = isBrowserFullscreen();
            if (wasFullscreenRef.current && !active) {
                toast.error('Fullscreen is required. Click "Enter Fullscreen" to continue.', {
                    duration: 5000,
                    icon: '⚠️',
                });
            }
            wasFullscreenRef.current = active;
            setNeedsFullscreen(!active);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
        };
    }, [view, testData]);

    useEffect(() => {
        if (view === 'success' || view === 'loading') {
            exitFullscreen();
        }
    }, [view, exitFullscreen]);

    useEffect(() => () => exitFullscreen(), [exitFullscreen]);

    // Tab Switch Warning Logic
    useEffect(() => {
        const tabSwitchWarning = isAssessmentFlagOn(testData?.assessment?.browser_lockdown);
        if (view === 'taking' && tabSwitchWarning) {
            const handleVisibilityChange = () => {
                if (document.hidden) {
                    toast.error("Warning: Tab switching is monitored. Please stay on the test page.", {
                        duration: 5000,
                        icon: '⚠️'
                    });
                }
            };
            document.addEventListener('visibilitychange', handleVisibilityChange);
            return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
        }
    }, [view, testData]);

    // Auto-save answers draft to localStorage
    useEffect(() => {
        if (packId) {
            localStorage.setItem(`test_draft_answers_${packId}`, JSON.stringify(answers));
        }
    }, [answers, packId]);

    // Auto-save flagged questions list to localStorage
    useEffect(() => {
        if (packId) {
            localStorage.setItem(`test_draft_flagged_${packId}`, JSON.stringify(Array.from(flagged)));
        }
    }, [flagged, packId]);

    // Auto-save current question index to localStorage
    useEffect(() => {
        if (packId) {
            localStorage.setItem(`test_draft_current_idx_${packId}`, String(currentIdx));
        }
    }, [currentIdx, packId]);

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    const handleAnswer = (option) => {
        const qId = questions[currentIdx].id;
        if (questions[currentIdx].type === "multi") {
            const currentAnswers = answers[qId] || [];
            if (currentAnswers.includes(option)) {
                setAnswers({ ...answers, [qId]: currentAnswers.filter(a => a !== option) });
            } else {
                setAnswers({ ...answers, [qId]: [...currentAnswers, option] });
            }
        } else {
            setAnswers({ ...answers, [qId]: (option || '').trim() });
        }
    };

    const toggleFlag = () => {
        const newFlagged = new Set(flagged);
        if (newFlagged.has(currentIdx)) {
            newFlagged.delete(currentIdx);
        } else {
            newFlagged.add(currentIdx);
        }
        setFlagged(newFlagged);
    };

    const handleNext = () => {
        if (currentIdx < questions.length - 1) {
            setCurrentIdx(prev => prev + 1);
        } else {
            setView('uploading');
        }
    };

    const validateSubmissionFile = (file) => {
        if (!file) return false;
        if (file.size > SUBMISSION_MAX_BYTES) {
            toast.error(`"${file.name}" exceeds 2 MB.`);
            return false;
        }
        return true;
    };

    const handleFileChange = (e) => {
        const picked = Array.from(e.target.files || []);
        if (!picked.length) return;
        const valid = [];
        picked.forEach((file) => {
            if (validateSubmissionFile(file)) {
                valid.push(file);
            }
        });
        if (!valid.length) {
            e.target.value = '';
            return;
        }
        setSubmissionFiles((prev) => {
            const updated = [...prev];
            valid.forEach((file) => {
                if (!updated.some((x) => x.name === file.name && x.size === file.size)) {
                    updated.push(file);
                }
            });
            return updated;
        });
        e.target.value = '';
    };

    const removeSubmissionFile = (file) => {
        setSubmissionFiles((prev) =>
            prev.filter((f) => !(f.name === file.name && f.size === file.size))
        );
    };

    const formatSubmissionFileSize = (bytes) => {
        if (!bytes) return '0 B';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    useEffect(() => {
        if (submissionFiles.length > 0) {
            setUploadError(false);
        }
    }, [submissionFiles]);

    const fileLabel = submissionFiles.length
        ? `${submissionFiles.length} file(s) selected`
        : 'No file chosen';

    const answeredCount = Object.keys(answers).filter(qId => {
        const ans = answers[qId];
        return Array.isArray(ans) ? ans.length > 0 : (ans !== undefined && ans !== '');
    }).length;

    const testInstructions = React.useMemo(
        () => parseTestInstructions(testData?.assessment?.instructions),
        [testData?.assessment?.instructions]
    );

    const currentQuestion = questions[currentIdx] || null;

    const handleSelectQuestion = useCallback((idx) => {
        const allowBacktracking = isAssessmentFlagOn(testData?.assessment?.allow_backtracking);
        if (idx < currentIdx && !allowBacktracking) {
            toast.error('Navigation control is enabled. You cannot go back to previous questions.');
            return;
        }
        setView('taking');
        setCurrentIdx(idx);
    }, [currentIdx, testData?.assessment?.allow_backtracking]);

    const CommonHeader = () => (
        <div className="assess-mod-student-exec-header execution-header">
            <div className="header-left">
                <div className="logo-section">
                    <FiShield className="logo-icon" />
                    <span className="logo-text">AssessHub</span>
                </div>
                <div className="divider-v"></div>
                <div className="test-info-header">
                    <div className="test-main-title">{testData?.template?.name || 'Loading...'}</div>
                    <div className="test-subtitle">Question {currentIdx + 1} of {questions.length}</div>
                </div>
            </div>

            <div className="header-center">
                <div className="stat-card">
                    <span className="stat-val">{testData?.template?.total_marks || 0} Marks</span>
                    <span className="stat-lbl">TOTAL MARKS</span>
                </div>
                <div className="stat-card">
                    <span className="stat-val pass">{testData?.template?.pass_mark || 0}</span>
                    <span className="stat-lbl">PASS MARK</span>
                </div>
                <div className="stat-card">
                    <span className="stat-val">{testData?.pack?.duration || 0} Mins</span>
                    <span className="stat-lbl">DURATION</span>
                </div>
            </div>

            <div className="header-right">
                <div className="timer-box">
                    <FiClock className="timer-icon" />
                    <span className="timer-text">{formatTime(timeLeft)}</span>
                </div>
                <button className="header-submit-btn" onClick={() => setView('uploading')}>
                    Submit Test <FiArrowRight size={14} />
                </button>
            </div>
        </div>
    );


    if (view === 'loading') {
        return (
            <div className="assess-mod-student-exec execution-layout" style={{ justifyContent: 'center', alignItems: 'center' }}>
                <div className="empty-state-container">
                    <div className="loading-spinner"></div>
                    <p className="empty-title">Preparing Test...</p>
                    <p className="empty-subtitle">Please wait while we set up your environment.</p>
                </div>
            </div>
        );
    }

    if (view === 'prep') {
        return (
            <div className="assess-mod-student-exec execution-layout">
                <FullscreenGate mode="start" onContinue={startTakingView} />
            </div>
        );
    }

    if (view === 'video') {
        return (
            <div className="assess-mod-student-exec execution-layout video-mode">
                <VideoInstructionView
                    files={getTestIntroVideosList(testData?.assessment)}
                    videoFinished={videoFinished}
                    setVideoFinished={setVideoFinished}
                    onStartExam={startTakingView}
                    videoRef={videoRef}
                    lastTime={lastTime}
                    setLastTime={setLastTime}
                />
            </div>
        );
    }

    if (view === 'success') {
        return (
            <div className="success-overlay">
                <div className="success-modal">
                    <div className="success-icon-wrapper">
                        <FiCheckCircle size={40} className="success-icon" />
                    </div>
                    <h2 className="success-title">Test Submitted Successfully!</h2>
                    <p className="success-msg">Your answers have been recorded. Results will be available shortly.</p>
                    <button
                        className="back-dashboard-btn"
                        onClick={() => {
                            exitFullscreen();
                            navigate('/assessment');
                        }}
                    >
                        Back to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="assess-mod-student-exec execution-layout">
            <CommonHeader />

            {view === 'taking' && needsFullscreen && isProctoredExam(testData) && (
                <FullscreenGate mode="resume" onContinue={enterExamFullscreen} />
            )}

            <div className="assess-mod-student-exec-body execution-body">
                <div className="assess-mod-student-exec-main main-content-area">
                    {view === 'taking' && (
                        <div className="question-container">
                            <div className="question-card">
                                <div className="card-header">
                                    <div className="q-header-meta">
                                        <div className="q-badge">Q{currentIdx + 1}</div>
                                        {currentQuestion?.pedagogy && (
                                            <span className="q-pedagogy-badge" title={currentQuestion.pedagogy}>
                                                {currentQuestion.pedagogy}
                                            </span>
                                        )}
                                    </div>
                                    <button className={`flag-btn ${flagged.has(currentIdx) ? 'active' : ''}`} onClick={toggleFlag}>
                                        <FiFlag /> Flag Question
                                    </button>
                                </div>

                                <div className="question-text-area">
                                    <div className="section-name-badge">{currentQuestion?.sectionName}</div>
                                    <h2 className="question-title">{currentQuestion?.text}</h2>
                                    <p className="question-hint">
                                        {currentQuestion?.type === 'subjective'
                                            ? 'Type your descriptive answer in the field below.'
                                            : (currentQuestion?.type === 'multi' ? 'Select all that apply.' : 'Select the correct option.')}
                                    </p>
                                </div>

                                <div className="options-grid">
                                    {currentQuestion?.type === 'subjective' ? (
                                        <div className="subjective-container">
                                            <textarea
                                                className="subjective-input"
                                                placeholder="Type your detailed answer here..."
                                                value={answers[currentQuestion?.id] || ''}
                                                onChange={(e) => setAnswers({ ...answers, [currentQuestion.id]: e.target.value })}
                                            ></textarea>
                                            <div className="input-footer">
                                                <span>{(answers[currentQuestion?.id] || '').length} characters</span>
                                                <FiFileText size={14} />
                                            </div>
                                        </div>
                                    ) : (
                                        currentQuestion?.options.map((opt, i) => {
                                            const isSelected = currentQuestion.type === "multi"
                                                ? (answers[currentQuestion.id] || []).includes(opt)
                                                : answers[currentQuestion.id] === opt;

                                            return (
                                                <button
                                                    key={i}
                                                    className={`option-btn ${isSelected ? 'selected' : ''}`}
                                                    onClick={() => handleAnswer(opt)}
                                                >
                                                    <div className="option-check"></div>
                                                    <span className="option-label">{opt}</span>
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                            </div>

                            <div className="nav-actions">
                                <button
                                    className="nav-prev"
                                    onClick={() => setCurrentIdx(prev => Math.max(0, prev - 1))}
                                    disabled={currentIdx === 0 || !isAssessmentFlagOn(testData?.assessment?.allow_backtracking)}
                                >
                                    <FiChevronLeft /> Previous Question
                                </button>
                                <button className="nav-next" onClick={handleNext}>
                                    {currentIdx === questions.length - 1 ? 'Finish Test' : 'Next Question'} <FiChevronRight />
                                </button>
                            </div>
                        </div>
                    )}

                    {view === 'uploading' && (
                        <div className="completion-overlay">
                            <div className="upload-card">
                                <div className="upload-header-row">
                                    <h2 className="upload-title">Final Submission</h2>
                                    <span className="upload-tag">OPTIONAL UPLOAD</span>
                                </div>
                                <p className="upload-instruction">You can upload one or multiple files before submitting. This is optional.</p>

                                <div className="upload-grid" style={{ gridTemplateColumns: '1fr' }}>
                                    <div className="upload-box" style={{ width: '100%' }}>
                                        <div className="box-label">UPLOAD FILES</div>
                                        <div className="file-input-wrapper">
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                multiple
                                                style={{ display: 'none' }}
                                                onChange={handleFileChange}
                                            />
                                            <button
                                                type="button"
                                                className="choose-file-btn"
                                                onClick={() => fileInputRef.current?.click()}
                                            >
                                                Choose File(s)
                                            </button>
                                            <span className="file-name">{fileLabel}</span>
                                        </div>
                                    </div>
                                </div>

                                {submissionFiles.length > 0 && (
                                    <ul className="submission-files-list">
                                        {submissionFiles.map((file, idx) => (
                                            <li
                                                key={`${file.name}-${file.size}-${idx}`}
                                                className="submission-file-item"
                                            >
                                                <i
                                                    className={`bi ${getFileIcon(file.name)} submission-file-item__icon`}
                                                    aria-hidden
                                                />
                                                <span className="submission-file-item__name" title={file.name}>
                                                    {file.name}
                                                </span>
                                                <span className="submission-file-item__size">
                                                    {formatSubmissionFileSize(file.size)}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="submission-file-remove"
                                                    onClick={() => removeSubmissionFile(file)}
                                                    aria-label={`Remove ${file.name}`}
                                                    title="Remove file"
                                                >
                                                    <FiTrash2 size={14} />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                <p className="upload-limit-text">
                                    Maximum file size: 2 MB each. Any format allowed.
                                    <br />
                                    {uploadError ? (
                                        <span style={{ color: '#ef4444', fontWeight: 'bold' }}>No files selected.</span>
                                    ) : (
                                        submissionFiles.length > 0
                                            ? `${submissionFiles.length} file(s) selected.`
                                            : 'No files selected.'
                                    )}
                                </p>

                                <div className="upload-actions">
                                    <button
                                        className="skip-btn"
                                        onClick={() => {
                                            setUploadError(false);
                                            setView('completing');
                                        }}
                                    >
                                        Skip
                                    </button>
                                    <button
                                        className="save-btn"
                                        onClick={() => {
                                            if (submissionFiles.length === 0) {
                                                setUploadError(true);
                                            } else {
                                                setUploadError(false);
                                                setView('completing');
                                            }
                                        }}
                                    >
                                        Save & Continue
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {view === 'completing' && (
                        <div className="completion-overlay">
                            <div className="completion-card">
                                <div className="completion-icon-wrapper">
                                    <FiCheckCircle size={40} />
                                </div>
                                <h2 className="completion-title">Test Completion</h2>
                                <p className="completion-msg">
                                    Your responses and any uploaded documents are ready for final submission.
                                    Please click 'Submit Test' to complete your test.
                                </p>
                                <div className="completion-actions">
                                    <button className="comp-back-btn" onClick={() => { startTakingView(); }}>Back</button>
                                    <button className="comp-submit-btn" onClick={submitFinal} disabled={isSubmitting}>
                                        {isSubmitting ? 'SUBMITTING...' : 'SUBMIT TEST'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <TestExecutionSidebar
                    testInstructions={testInstructions}
                    answeredCount={answeredCount}
                    questions={questions}
                    currentIdx={currentIdx}
                    answers={answers}
                    flagged={flagged}
                    onSelectQuestion={handleSelectQuestion}
                />
            </div>
        </div>
    );
};

export default TestExecutionPage;
